/**
 * Wire types for `/api/xcel-prelicensing/` (backend app `xcel_prelicensing`).
 * Field names are the backend's, unchanged.
 */

export type Capability = 'view' | 'refresh' | 'review' | 'manage';
export type XcelAccess = Record<Capability, boolean>;

export type RunType =
  | 'scheduled_daily_reconcile'
  | 'manual_full_reconcile'
  | 'mapping_pass'
  | 'manual_student_refresh'
  | 'republish_confirmed_progress'
  | 'connection_probe';

export type QueueableRunType = 'manual_full_reconcile' | 'mapping_pass' | 'republish_confirmed_progress';

export type RunState = 'queued' | 'running' | 'delayed' | 'completed' | 'partial' | 'failed' | 'cancelled';

export const ACTIVE_RUN_STATES: readonly RunState[] = ['queued', 'running', 'delayed'];

export interface XcelRun {
  id: number;
  run_type: RunType;
  state: RunState;
  stage: string;
  trigger_source: string;
  requested_by_id: number | null;
  requested_account_id: number | null;
  attempts: number;
  pages_total: number;
  pages_completed: number;
  students_seen: number;
  students_staged: number;
  students_confirmed: number;
  students_needing_review: number;
  projections_applied: number;
  projections_noop: number;
  projections_blocked: number;
  counts: Record<string, unknown>;
  error_code: string | null;
  retry_after_seconds: number | null;
  created_at: string;
  started_at: string | null;
  heartbeat_at: string | null;
  completed_at: string | null;
}

export interface QueuedRun {
  run: XcelRun;
  reused: boolean;
}

export interface XcelSettings {
  revision: number;
  settings_revision: number;
  provider_name: string;
  base_url: string;
  customer_identifier: string;
  root_location_id: number | null;
  include_children: boolean;
  page_size: number;
  secret_reference: string;
  api_key_configured: boolean;
  is_active: boolean;
  projection_enabled: boolean;
  single_refresh_freshness_seconds: number;
  daily_schedule_enabled: boolean;
  daily_run_time: string | null;
  schedule_timezone: string;
  last_scheduled_on: string | null;
  committed_generation: number;
}

export type SettingsPatch = Partial<
  Pick<
    XcelSettings,
    | 'customer_identifier'
    | 'root_location_id'
    | 'page_size'
    | 'secret_reference'
    | 'is_active'
    | 'projection_enabled'
    | 'single_refresh_freshness_seconds'
    | 'daily_schedule_enabled'
    | 'daily_run_time'
    | 'schedule_timezone'
  >
> & { expected_revision: number };

export type MappingStatus = 'confirmed' | 'auto_matched' | 'review_required' | 'unmatched';

export type MatchMethod =
  | 'automatic_identity_agent'
  | 'automatic_identity'
  | 'automatic_name_email_agent'
  | 'email'
  | 'phone'
  | 'multiple'
  | 'name'
  | 'manual_review';

export interface XcelStatus {
  is_active: boolean;
  projection_enabled: boolean;
  daily_schedule_enabled: boolean;
  daily_run_time: string | null;
  schedule_timezone: string;
  committed_generation: number;
  last_successful_full_run: XcelRun | null;
  active_runs: XcelRun[];
  students: { active: number; not_seen: number };
  mappings: Record<MappingStatus, number>;
}

export interface RunActivity {
  run: XcelRun;
  requests_count: number;
  requests: {
    endpoint_template: string;
    page_index: number | null;
    http_status: number | null;
    provider_success: boolean | null;
    duration_ms: number | null;
    retry_after_seconds: number | null;
    error_code: string;
    requested_at: string;
  }[];
  publication_outcomes: Record<string, number>;
}

export interface PersonRef {
  id: number;
  name: string;
  agency_code: string;
}

export interface MappingCandidate {
  account_id: number;
  exists: boolean;
  name: string;
  agency_code: string;
  email: string;
  phone: string;
  is_active: boolean;
  level: string;
  evidence: ('email' | 'phone' | 'name')[];
  leader: PersonRef | null;
  closest_md: PersonRef | null;
  closest_smd: PersonRef | null;
  hierarchy_incomplete: boolean;
  manager_validation: string | null;
  is_selected: boolean;
  /** Already confirmed to a different XCEL student; confirming it here would be a 409. */
  claimed_by_other_student: boolean;
  filter_match: boolean;
}

export interface MappingGroup {
  student_id: number;
  map_id: number;
  mapping_status: MappingStatus;
  match_method: MatchMethod | '';
  accounts_user_id: number | null;
  decided_at: string | null;
  review_note: string;
  xcel: {
    name: string;
    email: string;
    phone: string;
    manager_email: string;
    manager: PersonRef | null;
    current_state: 'active' | 'not_seen';
  };
  candidates: MappingCandidate[];
  evidence_summary: Record<string, number>;
}

export interface MappingFilters {
  state: MappingStatus | 'all';
  evidence: MatchMethod | 'all';
  search: string;
  leader: PersonRef | null;
  md: PersonRef | null;
  smd: PersonRef | null;
}

export interface MappingPage {
  results: MappingGroup[];
  next_cursor: string | null;
}

export interface MappingCounts {
  total: number;
  by_state: Record<MappingStatus, number>;
  by_evidence: Record<MatchMethod, number>;
  filtered_total: number;
}

export interface HierarchyOption extends PersonRef {
  level: string;
}

export type Decision =
  | { decision: 'confirm'; candidate_account_id: number; expected_map_id: number; note?: string }
  | { decision: 'mark_unmatched' | 'reopen'; expected_map_id: number; note?: string };

export interface DecisionResult {
  mapping: MappingGroup;
  publication: string | null;
}

export interface AccountProgress {
  account_id: number;
  mapping_state: MappingStatus | null;
  map_id: number | null;
  student_id: number | null;
  selected_program: {
    enrollment_id: string;
    program_name: string;
    selected_date: string;
    progress_all: number | null;
    final_exam_score: number | null;
  } | null;
  freshness: {
    last_success_at: string | null;
    detail_refreshed_at: string | null;
    source_state?: 'active' | 'not_seen';
  };
  licensing: {
    exists: boolean;
    xcel_completion_percent: string | null;
    xcel_final_score: string | null;
  };
  last_publication: { status: string; at: string; source_date: string | null } | null;
}

export interface AccountProgram {
  enrollment_id: string;
  program_name: string;
  current_state: 'active' | 'not_seen';
  start_date: string | null;
  due_date: string | null;
  end_date: string | null;
  completion_date: string | null;
  certificate_expiration_date: string | null;
  is_completed: boolean;
  progress_all: number | null;
  progress_required: number | null;
  final_exam_score: number | null;
  is_selected: boolean;
}

export interface AccountPrograms {
  count: number;
  results: AccountProgram[];
}

/** `POST accounts/<id>/refresh/`: a queued / reused run, or "already fresh". */
export type RefreshResult = QueuedRun | { run: null; fresh: true; last_success_at: string };

export interface XcelErrorBody {
  code: string;
  detail: string;
}
